/**
 * Launch accounts in the console (ADR 0071, ADR 0078): state.ext['launch-accounts'] from the hub,
 * kept live by SSE 'ext', and its changes. Accounts belong to one agent each (Claude Code, Codex,
 * Gemini CLI). In mock mode the demo has accounts of each agent, in memory.
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { DEFAULT_LAUNCH_ACCOUNT_AGENT, LAUNCH_ACCOUNTS_EXT, LAUNCH_ACCOUNT_AGENTS, LAUNCH_ACCOUNT_AGENT_LABEL, POOL_LAUNCH_ACCOUNT_ID, POOL_LAUNCH_ACCOUNT_LABEL, cleanLaunchPool, launchAccountAgent, launchAccountKey, launchAccountKindLabel, limitOf, orderedFallbacks } from '@orgus/protocol';
import { liveWindows } from '@orgus/protocol';
import type { AgentUsage, AgentUsageWindow, LaunchAccountTest } from '@orgus/protocol';
import type { LaunchAccount, LaunchAccountAgent, LaunchAccountDefaults, LaunchAccountInput, LaunchAccountLimit, LaunchAccountsState, LaunchFallbackInput, LaunchFolderStatus, LaunchPool, LaunchPoolInput, LaunchSignIn, TermSummary } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { openTerminal } from '../terminal/api.ts';
import { mockLaunchAccounts } from './mock.ts';

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const isMock = () => useOrgus.getState().mode === 'mock';
const now = () => useOrgus.getState().ext[LAUNCH_ACCOUNTS_EXT] as LaunchAccountsState | undefined;
const put = (s: LaunchAccountsState) => useOrgus.getState().applyExt({ ext: LAUNCH_ACCOUNTS_EXT, type: 'state', data: s });

export interface LaunchAccountsView {
  phase: 'loading' | 'error' | 'missing' | 'ready';
  state: LaunchAccountsState | null;
  mock: boolean;
}

/** In mock mode, seed the demo accounts once the demo state is ready. */
function useMockSeed(): void {
  const { ready, mock, missing } = useOrgus(useShallow((s) => ({ ready: s.phase === 'ready', mock: s.mode === 'mock', missing: !s.ext[LAUNCH_ACCOUNTS_EXT] })));
  useEffect(() => {
    if (!mock || !ready || !missing) return;
    const st = useOrgus.getState();
    put(mockLaunchAccounts(Object.values(st.machines), Object.keys(st.projects), Date.now()));
  }, [mock, ready, missing]);
}

export function useLaunchAccounts(): LaunchAccountsView {
  useMockSeed();
  const { phase, mode, state } = useOrgus(useShallow((s) => ({ phase: s.phase, mode: s.mode, state: (s.ext[LAUNCH_ACCOUNTS_EXT] as LaunchAccountsState | undefined) ?? null })));
  const mock = mode === 'mock';
  if (phase === 'loading' || phase === 'error') return { phase, state, mock };
  if (!state) return { phase: mock ? 'loading' : 'missing', state, mock };
  return { phase: 'ready', state, mock };
}

/** One agent's accounts, its default sign-in first (state order). */
export function accountsOf(state: Pick<LaunchAccountsState, 'accounts'>, agent: LaunchAccountAgent): LaunchAccount[] {
  return state.accounts.filter((a) => launchAccountAgent(a) === agent);
}

/** The agents launch pickers offer accounts for; Codex and Gemini CLI land dark until the agent applies them (ADR 0078). */
export const PICKER_AGENTS: readonly LaunchAccountAgent[] = ['claude-code'];

export interface LaunchAccountChoices {
  agent: LaunchAccountAgent;
  accounts: LaunchAccount[];
  defaults: Record<string, LaunchAccountDefaults>;
  /** The agent's pool (ADR 0088), when it has one. */
  pool: LaunchPool | null;
}

/**
 * The accounts a launch picker offers for one agent: none until that agent has two to choose
 * between (its default sign-in and one more). Null `agent`: the agent takes no account.
 */
export function launchAccountChoices(state: LaunchAccountsState | null, agent: LaunchAccountAgent | null, offered: readonly LaunchAccountAgent[] = PICKER_AGENTS): LaunchAccountChoices | null {
  if (!state || !agent || !offered.includes(agent)) return null;
  const accounts = accountsOf(state, agent);
  return accounts.length < 2 ? null : { agent, accounts, defaults: state.defaults, pool: state.pools?.[agent] ?? null };
}

/** The options an account picker offers: what applies by default, each account, and the pool once it exists. */
export function accountOptionsOf(c: LaunchAccountChoices, projectId: string): { value: string; label: string }[] {
  return [{ value: '', label: defaultChoiceLabel(c, projectId) }, ...c.accounts.map((a) => ({ value: a.id, label: a.name })), ...(c.pool ? [{ value: POOL_LAUNCH_ACCOUNT_ID, label: POOL_LAUNCH_ACCOUNT_LABEL }] : [])];
}

/** The id a picker's value sends with the launch: an account, the pool, or null for the default. */
export function pickedAccountId(c: LaunchAccountChoices | null | undefined, value: string): string | null {
  if (!c || !value) return null;
  if (value === POOL_LAUNCH_ACCOUNT_ID) return c.pool ? value : null;
  return c.accounts.some((a) => a.id === value) ? value : null;
}

/** An account's active limit (ADR 0088), anywhere, or null. */
export function accountLimit(state: Pick<LaunchAccountsState, 'limits'>, accountId: string): LaunchAccountLimit | null {
  return limitOf(state.limits ?? [], accountId, null, Date.now());
}

export function useLaunchAccountChoices(agent: LaunchAccountAgent | null = DEFAULT_LAUNCH_ACCOUNT_AGENT): LaunchAccountChoices | null {
  const { state } = useLaunchAccounts();
  return launchAccountChoices(state, agent);
}

/** Every agent that has two or more accounts, for a picker whose account decides the agent (missions). */
export function useAllLaunchAccountChoices(): LaunchAccountChoices[] {
  const { state } = useLaunchAccounts();
  return LAUNCH_ACCOUNT_AGENTS.map((g) => launchAccountChoices(state, g)).filter((c): c is LaunchAccountChoices => !!c);
}

/**
 * A mission's account choices (ADR 0078): every agent's accounts in one list, since the account
 * decides the agent of an unpinned mission. Built-in sign-ins are named after their agent.
 */
export function missionAccountOptions(choices: LaunchAccountChoices[], projectId: string): { value: string; label: string; agent: LaunchAccountAgent | null }[] {
  const defaults = choices.flatMap((c) => {
    const id = c.defaults[projectId]?.[c.agent];
    const a = id ? c.accounts.find((x) => x.id === id) : undefined;
    return a ? [a.builtIn ? `${LAUNCH_ACCOUNT_AGENT_LABEL[c.agent]} default sign-in` : a.name] : [];
  });
  const first = { value: '', label: defaults.length ? `Project default: ${defaults.join(', ')}` : 'As set on the machine', agent: null };
  // 'pool' means the agent's pool: the Claude Code one for an unpinned mission (ADR 0088).
  const claude = choices.find((c) => c.agent === DEFAULT_LAUNCH_ACCOUNT_AGENT);
  const pool = claude?.pool ? [{ value: POOL_LAUNCH_ACCOUNT_ID, label: `${POOL_LAUNCH_ACCOUNT_LABEL} (${LAUNCH_ACCOUNT_AGENT_LABEL[claude.agent]})`, agent: claude.agent }] : [];
  return [first, ...choices.flatMap((c) => c.accounts.map((a) => ({ value: a.id, label: a.builtIn ? `${LAUNCH_ACCOUNT_AGENT_LABEL[c.agent]} default sign-in` : a.name, agent: c.agent }))), ...pool];
}

/** What a launch with no account named runs on, for the picker's first option. */
export function defaultChoiceLabel(state: Pick<LaunchAccountChoices, 'accounts' | 'defaults'> & { agent?: LaunchAccountAgent; pool?: LaunchPool | null }, projectId: string): string {
  const id = state.defaults[projectId]?.[state.agent ?? DEFAULT_LAUNCH_ACCOUNT_AGENT];
  if (id === POOL_LAUNCH_ACCOUNT_ID) return 'Project default: the pool';
  const a = id ? state.accounts.find((x) => x.id === id) : undefined;
  if (a) return `Project default: ${a.name}`;
  return state.pool?.whenUnset ? 'From the pool' : 'As set on the machine';
}

/** A subscription account's folders on each machine, with what the machine reported. */
export function foldersOf(state: LaunchAccountsState, a: LaunchAccount): { machineId: string; path: string | null; status: LaunchFolderStatus | null }[] {
  const reported = state.folders.filter((f) => f.accountId === a.id);
  if (a.builtIn) return reported.map((f) => ({ machineId: f.machineId, path: f.path, status: f }));
  return Object.entries(a.folders).map(([machineId, path]) => ({ machineId, path, status: reported.find((f) => f.machineId === machineId && f.path === path) ?? null }));
}

const samePath = (a: string, b: string) => a.replace(/[\\/]+$/, '').replace(/\\/g, '/').toLowerCase() === b.replace(/[\\/]+$/, '').replace(/\\/g, '/').toLowerCase();

/**
 * The sign-in terminal of one folder (ADR 0071): the one this row opened (`openedId`), else the
 * newest sign-in terminal on that machine whose folder it is, so a reopened drawer finds it again.
 */
export function signInTermFor(terms: TermSummary[], machineId: string, path: string, openedId: string | null): TermSummary | undefined {
  if (openedId) {
    const mine = terms.find((t) => t.id === openedId);
    if (mine) return mine;
  }
  return terms
    .filter((t) => t.kind === 'sign-in' && t.machineId === machineId && t.state !== 'exited' && samePath(t.cwd, path))
    .sort((a, b) => b.startedAt - a.startedAt)[0];
}

/** What a folder row shows about signing in: the terminal pane, the Sign in action, and Stop or Close for the pane. */
export function signInView(state: LaunchFolderStatus['state'] | null, term: Pick<TermSummary, 'state'> | undefined): { pane: boolean; signIn: boolean; close: boolean } {
  const signedIn = state === 'signed-in';
  const live = !!term && term.state !== 'exited';
  // Signed in and the login has ended: the row says so, the pane goes. Otherwise the pane stays, with Stop or Close.
  const pane = !!term && (live || !signedIn);
  // Offered again after a login that ended without signing in; the agent checks the folder itself.
  const signIn = !live && (state === null || state === 'signed-out');
  return { pane, signIn, close: pane };
}

/**
 * Open a sign-in terminal for the account on that machine (ADR 0088 amendment: claude setup-token;
 * the hub keeps the token, so the account then runs on every machine). In mock mode the account
 * signs in a few seconds later.
 */
export async function startSignIn(accountId: string, machineId: string): Promise<string> {
  const r = await openTerminal({ machineId, mode: 'sign-in', launchAccountId: accountId, cols: 100, rows: 20 });
  if (isMock()) {
    setTimeout(() => {
      const cur = now();
      if (!cur) return;
      const t = Date.now();
      put({ ...cur, accounts: cur.accounts.map((a) => (a.id === accountId ? { ...a, secret: { fingerprint: fp(`demo-${accountId}`), setAt: t, setBy: 'machine:demo' }, updatedAt: t } : a)) });
    }, 4_000);
  }
  return r.term.id;
}

/** The machine a sign-in runs on when the person did not pick one: the first online machine. */
export function signInMachine(machines: { id: string; online: boolean }[]): string | null {
  return machines.find((m) => m.online)?.id ?? null;
}

/** The sign-in terminal the drawer shows: the one it opened, while it runs or until the account is signed in. */
export function tokenSignInView(a: Pick<LaunchAccount, 'secret'>, term: Pick<TermSummary, 'state'> | undefined): { pane: boolean; signIn: boolean; close: boolean } {
  const live = !!term && term.state !== 'exited';
  const pane = !!term && (live || !a.secret);
  return { pane, signIn: !live, close: pane };
}

// ── Changes ─────────────────────────────────────────────────────────────────

const fp = (v: string) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < v.length; i++) h = Math.imul(h ^ v.charCodeAt(i), 0x01000193) >>> 0;
  return h.toString(16).padStart(8, '0').slice(0, 8);
};

function mockSave(input: Partial<LaunchAccountInput>, id?: string): LaunchAccount {
  const cur = now()!;
  const t = Date.now();
  const prev = id ? cur.accounts.find((a) => a.id === id) : undefined;
  if (!prev && cur.accounts.some((a) => a.name.toLowerCase() === input.name?.trim().toLowerCase())) throw new Error(`An account named ${input.name} already exists.`);
  const secret = input.secret ? { fingerprint: fp(input.secret), setAt: t, setBy: 'you' } : input.secret === '' ? null : (prev?.secret ?? null);
  const base: LaunchAccount = prev ?? { id: `la_DEMO${t.toString(36).toUpperCase()}`, name: '', agent: input.agent ?? DEFAULT_LAUNCH_ACCOUNT_AGENT, kind: input.kind!, settings: {}, folders: {}, secret: null, accountKey: null, createdBy: 'you', createdAt: t };
  // No name: the kind's, numbered, as the hub names it.
  if (!prev && !input.name) {
    const kindName = launchAccountKindLabel(base.agent, base.kind);
    let n = kindName;
    for (let i = 2; cur.accounts.some((x) => x.name.toLowerCase() === n.toLowerCase()); i++) n = `${kindName} ${i}`;
    base.name = n;
  }
  const next: LaunchAccount = { ...base, ...(input.name ? { name: input.name.trim() } : {}), ...(input.settings ? { settings: input.settings } : {}), secret, ...(prev ? { updatedBy: 'you', updatedAt: t } : {}) };
  next.accountKey = launchAccountKey({ agent: next.agent, kind: next.kind, settings: next.settings, fingerprint: next.secret?.fingerprint ?? null, orgKey: prev?.accountKey ?? null });
  put({ ...cur, accounts: prev ? cur.accounts.map((a) => (a.id === id ? next : a)) : [...cur.accounts, next] });
  return next;
}

export async function saveLaunchAccount(input: Partial<LaunchAccountInput>, id?: string): Promise<LaunchAccount> {
  if (isMock()) {
    await wait(300);
    return mockSave(input, id);
  }
  const res = await api<{ account: LaunchAccount; state: LaunchAccountsState }>(id ? `/api/launch-accounts/${encodeURIComponent(id)}` : '/api/launch-accounts', { method: id ? 'PUT' : 'POST', body: input });
  put(res.state);
  return res.account;
}

export async function deleteLaunchAccount(id: string): Promise<void> {
  if (isMock()) {
    await wait(300);
    const cur = now()!;
    const defaults = Object.fromEntries(
      Object.entries(cur.defaults)
        .map(([p, d]) => [p, Object.fromEntries(Object.entries(d).filter(([, a]) => a !== id))] as const)
        .filter(([, d]) => Object.keys(d).length),
    );
    put({ ...cur, accounts: cur.accounts.filter((a) => a.id !== id), defaults, folders: cur.folders.filter((f) => f.accountId !== id) });
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }));
}

/** Set a subscription account's folder on a machine: a path there, or `create` one under the agent's home. */
export async function setLaunchFolder(id: string, machineId: string, body: { path: string } | { create: true }): Promise<void> {
  if (isMock()) {
    await wait(400);
    const cur = now()!;
    const dir = { 'claude-code': 'claude-accounts', codex: 'codex-accounts', 'gemini-cli': 'gemini-accounts' }[launchAccountAgent(cur.accounts.find((a) => a.id === id))];
    const path = 'path' in body ? body.path : `C:\\Users\\you\\.orgus\\${dir}\\${id}`;
    const status: LaunchFolderStatus = { machineId, accountId: id, path, state: 'signed-out', account: null, checkedAt: Date.now() };
    put({
      ...cur,
      accounts: cur.accounts.map((a) => (a.id === id ? { ...a, folders: { ...a.folders, [machineId]: path } } : a)),
      folders: [...cur.folders.filter((f) => !(f.accountId === id && f.machineId === machineId)), status],
    });
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(id)}/folders/${encodeURIComponent(machineId)}`, { method: 'PUT', body }));
}

// ── ChatGPT and Google sign-in on a machine (ADR 0108) ──────────────────────

/** The sign-in of an account on a machine, if one runs or just ended. */
export function machineSignInOf(state: Pick<LaunchAccountsState, 'signIns'>, accountId: string, machineId: string): LaunchSignIn | null {
  return state.signIns?.find((s) => s.accountId === accountId && s.machineId === machineId) ?? null;
}

/** What one machine's row of a ChatGPT or Google subscription offers. */
export function machineSignInView(s: Pick<LaunchSignIn, 'step'> | null, folder: LaunchFolderStatus['state'] | null, online: boolean): { running: boolean; signIn: boolean; again: boolean } {
  const running = !!s && s.step !== 'done' && s.step !== 'failed';
  const signedIn = folder === 'signed-in';
  return { running, signIn: !running && online && !signedIn && folder !== 'not-allowed', again: !running && online && signedIn };
}

const mockSignIn = (accountId: string, machineId: string, patch: Partial<LaunchSignIn>) => {
  const cur = now();
  if (!cur) return;
  const a = cur.accounts.find((x) => x.id === accountId);
  const prev = machineSignInOf(cur, accountId, machineId);
  const t = Date.now();
  const s: LaunchSignIn = { accountId, machineId, agent: launchAccountAgent(a), step: 'starting', url: null, code: null, error: null, startedAt: prev?.startedAt ?? t, ...prev, ...patch, updatedAt: t };
  put({ ...cur, signIns: [...(cur.signIns ?? []).filter((x) => !(x.accountId === accountId && x.machineId === machineId)), s] });
};

const mockSignedIn = (accountId: string, machineId: string) => {
  const cur = now();
  if (!cur) return;
  const a = cur.accounts.find((x) => x.id === accountId);
  const dir = launchAccountAgent(a) === 'codex' ? 'codex-accounts' : 'gemini-accounts';
  const path = `/home/orgus/.orgus/${dir}/${accountId}`;
  const status: LaunchFolderStatus = { machineId, accountId, path, state: 'signed-in', account: null, checkedAt: Date.now() };
  put({
    ...cur,
    accounts: cur.accounts.map((x) => (x.id === accountId ? { ...x, folders: { ...x.folders, [machineId]: path } } : x)),
    folders: [...cur.folders.filter((f) => !(f.accountId === accountId && f.machineId === machineId)), status],
  });
  mockSignIn(accountId, machineId, { step: 'done', url: null, code: null });
};

/** Start the sign-in on that machine: the agent runs codex login --device-auth or gemini there. */
export async function startMachineSignIn(accountId: string, machineId: string): Promise<void> {
  if (isMock()) {
    mockSignIn(accountId, machineId, { step: 'starting', url: null, code: null, error: null, startedAt: Date.now() });
    await wait(600);
    const codex = launchAccountAgent(now()?.accounts.find((x) => x.id === accountId)) === 'codex';
    mockSignIn(accountId, machineId, codex ? { step: 'open', url: 'https://auth.openai.com/codex/device', code: 'K7QD-4M2XP' } : { step: 'paste', url: 'https://accounts.google.com/o/oauth2/v2/auth?client_id=demo' });
    if (codex) setTimeout(() => mockSignedIn(accountId, machineId), 8_000);
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(accountId)}/sign-in/${encodeURIComponent(machineId)}`, { method: 'POST', body: {} }));
}

/** Send the code Google showed to the machine's sign-in. It is never kept. */
export async function sendMachineSignInCode(accountId: string, machineId: string, code: string): Promise<void> {
  if (isMock()) {
    mockSignIn(accountId, machineId, { step: 'starting' });
    await wait(800);
    mockSignedIn(accountId, machineId);
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(accountId)}/sign-in/${encodeURIComponent(machineId)}/code`, { method: 'POST', body: { code } }));
}

/** Stop the sign-in on that machine (or clear one that ended). */
export async function stopMachineSignIn(accountId: string, machineId: string): Promise<void> {
  if (isMock()) {
    const cur = now();
    if (cur) put({ ...cur, signIns: (cur.signIns ?? []).filter((x) => !(x.accountId === accountId && x.machineId === machineId)) });
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(accountId)}/sign-in/${encodeURIComponent(machineId)}`, { method: 'DELETE' }));
}

export async function removeLaunchFolder(id: string, machineId: string): Promise<void> {
  if (isMock()) {
    await wait(300);
    const cur = now()!;
    put({
      ...cur,
      accounts: cur.accounts.map((a) => {
        if (a.id !== id) return a;
        const { [machineId]: _gone, ...folders } = a.folders;
        return { ...a, folders };
      }),
      folders: cur.folders.filter((f) => !(f.accountId === id && f.machineId === machineId)),
    });
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(id)}/folders/${encodeURIComponent(machineId)}`, { method: 'DELETE' }));
}

// ── Usage (ADR 0088) ─────────────────────────────────────────────────────────

/** An account's usage readings, newest first: its own, or each machine's own sign-in for the default. */
export function accountUsages(list: readonly AgentUsage[] | undefined, a: Pick<LaunchAccount, 'id' | 'builtIn' | 'agent'>): AgentUsage[] {
  const agent = launchAccountAgent(a);
  return (list ?? []).filter((u) => (a.builtIn ? !u.accountId && u.agentKind === agent : u.accountId === a.id)).sort((x, y) => y.at - x.at);
}

/** The windows a row shows: the current session, then the fullest other window, each with a percent. */
export function rowWindows(u: AgentUsage | null | undefined, now: number): AgentUsageWindow[] {
  if (!u) return [];
  // Every live window, at most four: the session, a day, the week, then each model week, fullest first.
  const order = (w: AgentUsageWindow) => (w.kind === 'session' || w.kind === 'five_hour' ? 0 : w.kind === 'daily' ? 1 : w.kind === 'weekly_all' || w.kind === 'seven_day' ? 2 : 3);
  return [...liveWindows(u, now)].sort((a, b) => order(a) - order(b) || (b.percent ?? -1) - (a.percent ?? -1)).slice(0, 4);
}

/** A window's name in two to five letters, under its ring: '5h', 'Week', 'Opus'. */
export function windowShort(w: Pick<AgentUsageWindow, 'kind' | 'scope'>): string {
  if (w.kind === 'session' || w.kind === 'five_hour') return '5h';
  if (w.kind === 'daily') return 'Day';
  if (w.scope) return w.scope.length <= 6 ? w.scope : w.scope.slice(0, 5);
  return 'Week';
}

/** Test an account (ADR 0088 decision 10): one tiny request on it on a machine. In mock mode it works after a moment. */
export async function testAccount(id: string): Promise<LaunchAccountTest> {
  if (isMock()) {
    await wait(1200);
    const cur = now()!;
    const test: LaunchAccountTest = { ok: true, summary: 'Answered in 2.4 s.', cause: null, durationMs: 2400, model: 'claude-sonnet-5-5', at: Date.now(), machineId: 'demo', by: 'you' };
    put({ ...cur, tests: { ...(cur.tests ?? {}), [id]: test } });
    return test;
  }
  const res = await api<{ test: LaunchAccountTest; state: LaunchAccountsState }>(`/api/launch-accounts/${encodeURIComponent(id)}/test`, { method: 'POST', body: {} });
  put(res.state);
  return res.test;
}

/** Read every account's usage now (Refresh usage). In mock mode the demo readings move a little. */
export async function refreshUsage(): Promise<void> {
  if (isMock()) {
    const cur = now();
    if (!cur) return;
    const t = Date.now();
    put({ ...cur, usageRefresh: { startedAt: t, endedAt: null, machines: 1, read: 0 } });
    await wait(1500);
    const after = now();
    if (after) put({ ...after, usageRefresh: { startedAt: t, endedAt: Date.now(), machines: 1, read: 2 } });
    return;
  }
  put(await api<LaunchAccountsState>('/api/launch-accounts/usage/refresh', { method: 'POST' }));
}

/** Set one agent's pool (ADR 0088): members in order, strategy, whether it stands in. An empty member list removes it. */
export async function savePool(agent: LaunchAccountAgent, input: LaunchPoolInput): Promise<void> {
  if (isMock()) {
    await wait(300);
    const cur = now()!;
    const next = cleanLaunchPool(input, cur.pools[agent]);
    const pools = { ...cur.pools };
    if (next) pools[agent] = next;
    else delete pools[agent];
    put({ ...cur, pools });
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/pools/${encodeURIComponent(agent)}`, { method: 'PUT', body: input }));
}

/** Forget an account's limits (ADR 0088): the pool may pick it again now. */
export async function clearLimit(accountId: string): Promise<void> {
  if (isMock()) {
    await wait(200);
    const cur = now()!;
    put({ ...cur, limits: cur.limits.filter((l) => l.accountId !== accountId) });
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(accountId)}/limit`, { method: 'DELETE' }));
}

// ── Fallback: a per-token account that takes over when a subscription runs out (ADR 0071 decision 9) ─

/** The daily caps the drawer offers, in USD; a cap set through the API that is not listed is added. */
export const FALLBACK_CAPS: readonly number[] = [10, 25, 50, 100, 250, 500];

export function capChoices(current: number | null): number[] {
  return current != null && !FALLBACK_CAPS.includes(current) ? [...FALLBACK_CAPS, current].sort((a, b) => a - b) : [...FALLBACK_CAPS];
}

/** The state after a fallback change, as the hub makes it: fallback accounts numbered 1..n in their order. */
export function applyFallback(state: LaunchAccountsState, id: string, input: LaunchFallbackInput, by: string, at: number): LaunchAccountsState {
  const prev = state.accounts.find((a) => a.id === id)?.fallback ?? null;
  const next = input.on
    ? { projects: input.projects === undefined ? (prev?.projects ?? null) : input.projects, order: prev?.order ?? Number.MAX_SAFE_INTEGER, dailyCapUsd: input.dailyCapUsd === undefined ? (prev?.dailyCapUsd ?? null) : input.dailyCapUsd, setBy: by, setAt: at }
    : null;
  const accounts = state.accounts.map((a) => (a.id === id ? { ...a, fallback: next } : a));
  const others = orderedFallbacks(accounts).filter((a) => a.id !== id);
  const self = accounts.find((a) => a.id === id && a.fallback);
  if (self) others.splice(Math.max(0, Math.min(others.length, (input.order ?? (prev ? prev.order : others.length + 1)) - 1)), 0, self);
  const order = new Map(others.map((a, i) => [a.id, i + 1]));
  return { ...state, accounts: accounts.map((a) => (a.fallback && order.has(a.id) ? { ...a, fallback: { ...a.fallback, order: order.get(a.id)! } } : a)) };
}

export async function setLaunchFallback(id: string, input: LaunchFallbackInput): Promise<void> {
  if (isMock()) {
    await wait(250);
    put(applyFallback(now()!, id, input, 'you', Date.now()));
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/${encodeURIComponent(id)}/fallback`, { method: 'PUT', body: input }));
}

/** Set or clear one project's default account of one agent ('pool': the agent's pool). */
export async function setProjectDefault(projectId: string, accountId: string | null, agent: LaunchAccountAgent = DEFAULT_LAUNCH_ACCOUNT_AGENT): Promise<void> {
  if (isMock()) {
    await wait(200);
    const cur = now()!;
    const one: LaunchAccountDefaults = { ...(cur.defaults[projectId] ?? {}) };
    if (accountId) one[agent] = accountId;
    else delete one[agent];
    const defaults = { ...cur.defaults, [projectId]: one };
    if (!Object.keys(one).length) delete defaults[projectId];
    put({ ...cur, defaults });
    return;
  }
  put(await api<LaunchAccountsState>(`/api/launch-accounts/defaults/${encodeURIComponent(projectId)}`, { method: 'PUT', body: { accountId, agent } }));
}
